/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import isEmpty from 'lodash/isEmpty';

import { SearchActions, Spinner, Switch } from 'sarsaparilla';
import { ButtonSanity } from 'sarsaparilla/src/button-sanity';

import { useAuthState } from 'shared-ui/auth';
import { useTripPreferences } from '../../components/tripPreferences/TripPreferenceContext';
import { EditTripPreferencesDialog } from './EditTripPreferencesDialog';
import type { PendingFiltersAction } from './pendingFilters';
import styles from './TripPreferences.module.scss';

interface TripPreferencesProps {
    onLoginClick: () => void;
    /** Whether the pending filters include the trip preferences */
    isEnabled: boolean;
    onChange: React.Dispatch<PendingFiltersAction>;
}

type TripPreferenceState =
    'logged-out' | 'loading' | 'has-preferences' | 'no-preferences';

const tripPreferenceStateToLabel: Record<TripPreferenceState, string> = {
    'logged-out': 'Log in to save your most-used filters',
    loading: 'Loading your trip preferences ...',
    'has-preferences': 'Quickly apply your most-used filters',
    'no-preferences': 'Quickly apply your most-used filters',
};

/**
 * Trip preferences at the top of the filters dialog. Turning them on or off
 * only changes the pending filters. Must be rendered inside a
 * `TripPreferenceProvider`.
 */
export function TripPreferences({
    onLoginClick,
    isEnabled,
    onChange,
}: TripPreferencesProps) {
    const { isRegisteredUser } = useAuthState();
    const { preferences, loaded, isOpenPreferencesModal, onPreferenceModal } =
        useTripPreferences();

    const onToggle = () => {
        onChange(
            isEnabled
                ? { type: 'disableTripPreferences', preferences }
                : { type: 'enableTripPreferences', preferences }
        );
    };

    // `preferences` is still the saved set from before the edit here
    const onPreferencesSaved = (next: string[]) => {
        onChange({ type: 'replaceTripPreferences', previous: preferences, next });
    };

    // Lives here rather than in the dialog so unsaved selections stay when it
    // closes, the same as in the desktop trip preferences modal
    const [checked, setChecked] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (
            typeof preferences === 'object' &&
            !isEmpty(preferences) &&
            preferences?.length > 0 &&
            !isOpenPreferencesModal
        ) {
            setChecked(preferences);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferences]);

    const openPreferencesDialog = () => {
        SearchActions.trackRunSearch(
            {
                category: 'Search Results Filters',
                action: 'Open Trip Preferences Modal',
                label: 'Manage Trip Preferences',
            },
            () => {}
        );
        onPreferenceModal(true);
    };

    let actions: React.ReactNode;

    const tripPreferenceState: TripPreferenceState = !isRegisteredUser
        ? 'logged-out'
        : !loaded
          ? 'loading'
          : preferences.length > 0
            ? 'has-preferences'
            : 'no-preferences';

    if (tripPreferenceState === 'logged-out') {
        actions = (
            <ButtonSanity
                appearance="soft-brand"
                size="sm"
                padding="tight"
                aria-label="Log in to use Trip Preferences"
                onClick={onLoginClick}
            >
                Log in
            </ButtonSanity>
        );
    } else if (tripPreferenceState === 'loading') {
        // Spinner instead of Set up while the saved preferences load, so nobody who
        // has them is offered Set up
        actions = (
            <Spinner
                size="xs"
                color="brand"
                isCentered={false}
                screenReaderText="Loading trip preferences"
            />
        );
    } else if (tripPreferenceState === 'no-preferences') {
        actions = (
            <ButtonSanity
                appearance="soft-brand"
                size="sm"
                padding="tight"
                aria-label="Set up Trip Preferences"
                onClick={openPreferencesDialog}
            >
                Set up
            </ButtonSanity>
        );
    } else {
        actions = (
            <>
                <ButtonSanity
                    appearance="soft-brand"
                    size="xs"
                    padding="loose"
                    corners="rounded"
                    aria-label="Edit Trip Preferences"
                    onClick={openPreferencesDialog}
                >
                    Edit
                </ButtonSanity>
                <Switch
                    isSelected={isEnabled}
                    label="Apply Trip Preferences"
                    isLabelVisible={false}
                    size="md"
                    onChange={onToggle}
                    id="trip-toggle-preferences"
                    clickTagCategory="Search Results Filters"
                    clickTagLabel="Apply Trip Preferences Toggle"
                />
            </>
        );
    }

    return (
        <>
            <div className={styles.TripPreferences}>
                <div>
                    <h2 className="text-lg font-weight-semibold mb-0">
                        Trip Preferences
                    </h2>
                    <div className="text-sm color-text-03">
                        {tripPreferenceStateToLabel[tripPreferenceState]}
                    </div>
                </div>
                <div className={styles.Actions}>{actions}</div>
            </div>

            {isRegisteredUser && (
                <EditTripPreferencesDialog
                    isOpen={isOpenPreferencesModal}
                    onOpenChange={onPreferenceModal}
                    checked={checked}
                    onChange={setChecked}
                    preferences={preferences}
                    onSaved={onPreferencesSaved}
                />
            )}
        </>
    );
}
